import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GameCard from './GameCard';
import AddGameModal from './AddGameModal';
import QuizGame from './QuizGame/QuizGameNew';
import QuestionManager from './QuizGame/QuestionManager';
import AIQuizGenerator from './QuizGame/AIQuizGenerator';
import OnlinePlayersLobby from './QuizGame/OnlinePlayersLobby';
import AdminQuizDashboard from './QuizGame/AdminQuizDashboard';


const GamesManager = ({ userRole, userClass, userName, userEmail, userId }) => {
  const [games, setGames] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentView, setCurrentView] = useState('games'); // games, quiz, questionManager, ai-quiz, online-lobby, live-challenge, admin-dashboard
  const [gameData, setGameData] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);

  useEffect(() => {
    fetchGames();
    fetchAvailableClasses();
    registerAsOnline();
    
    // Listen for close game messages
    const handleMessage = (event) => {
      if (event.data.type === 'closeGame') {
        setSelectedGame(null);
      }
    };
    window.addEventListener('message', handleMessage);
    
    return () => {
      unregisterFromOnline();
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const registerAsOnline = async () => {
    try {
      if (!userName || !userRole) return;
      
      const uniqueId = userId || userName + '_' + userRole;
      const displayName = userName || userEmail || `User_${uniqueId}`;
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await fetch(`${API_BASE}/api/users/online-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId: uniqueId, 
          name: displayName, 
          email: userEmail,
          role: userRole, 
          class: userClass 
        })
      });
      console.log('🎉 Registered as online:', userName);
    } catch (error) {
      console.error('❌ Error registering as online:', error);
    }
  };

  const unregisterFromOnline = () => {
    // Users automatically timeout after 30 seconds of inactivity
  };

  const fetchGames = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE}/api/games`);
      const data = await response.json();
      setGames(data);
    } catch (error) {
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableClasses = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE}/api/classes`);
      const data = await response.json();
      setAvailableClasses(data.map(cls => cls.name));
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const handleAddGame = async (gameData) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const gameWithCreator = {
        ...gameData,
        createdBy: userName || 'Unknown',
        userRole: userRole
      };
      const response = await fetch(`${API_BASE}/api/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gameWithCreator)
      });
      if (response.ok) {
        fetchGames();
        setShowAddModal(false);
      } else {
        const error = await response.text();
        console.error('Server error:', error);
      }
    } catch (error) {
      console.error('Error adding game:', error);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (window.confirm('Are you sure you want to delete this game?')) {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${API_BASE}/api/games/${gameId}?userRole=${userRole}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          fetchGames();
        }
      } catch (error) {
        console.error('Error deleting game:', error);
      }
    }
  };

  const filteredGames = games.filter(game => {
    // Class-based filtering
    if (userRole === 'student' || userRole === 'teacher') {
      // Students and teachers can only see games for their class or 'All'
      if (game.targetClass && game.targetClass !== 'All' && game.targetClass !== userClass) {
        return false;
      }
    }
    
    // Category filtering
    if (filter === 'all') return true;
    if (filter === 'educational') return game.category === 'educational';
    if (filter === 'fun') return game.category === 'fun';
    if (filter === 'puzzle') return game.category === 'puzzle';
    return true;
  });

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Loading games...</div>;
  }

  if (currentView === 'quiz') {
    return (
      <QuizGame 
        userRole={userRole} 
        userClass={userClass}
        userName={userName}
        userEmail={userEmail}
        userId={userId}
        gameData={gameData}
        onExit={() => {
          setCurrentView('games');
          setGameData(null);
        }} 
      />
    );
  }
  
  if (currentView === 'ai-quiz') {
    return (
      <AIQuizGenerator
        onQuestionsGenerated={(questions) => {
          setGameData({ mode: 'single', questions });
          setCurrentView('quiz');
        }}
        onClose={() => setCurrentView('games')}
      />
    );
  }
  
  if (currentView === 'online-lobby') {
    return (
      <OnlinePlayersLobby
        userRole={userRole}
        userName={userName}
        userClass={userClass}
        userEmail={userEmail}
        userId={userId}
        onStartGame={(gameData) => {
          setGameData(gameData);
          setCurrentView('quiz');
        }}
        onExit={() => setCurrentView('games')}
      />
    );
  }
  


  if (currentView === 'questionManager') {
    return (
      <QuestionManager 
        userRole={userRole} 
        onBack={() => setCurrentView('games')} 
      />
    );
  }

  if (currentView === 'admin-dashboard') {
    return (
      <AdminQuizDashboard 
        userRole={userRole} 
        onBack={() => setCurrentView('games')} 
      />
    );
  }

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      minHeight: '100vh',
      fontFamily: 'Georgia, serif'
    }}>
      {/* Professional Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        color: 'white',
        padding: '24px',
        borderRadius: '0 0 16px 16px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(30, 64, 175, 0.15)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '28px', 
            fontWeight: '600',
            fontFamily: 'Georgia, serif',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>🎓 Educational Games Portal</h1>
          <p style={{ 
            margin: 0, 
            fontSize: '16px', 
            opacity: 0.9,
            fontFamily: 'Georgia, serif'
          }}>Interactive learning experiences for enhanced education</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        {/* Game Categories Grid */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            margin: '0 0 20px 0', 
            color: '#1e40af', 
            fontSize: '20px',
            fontFamily: 'Georgia, serif',
            fontWeight: '600',
            textAlign: 'center'
          }}>🎮 Game Categories</h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
          }}>
            <button
              onClick={() => setCurrentView('quiz')}
              style={{
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                color: 'white',
                border: 'none',
                padding: '16px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Georgia, serif',
                boxShadow: '0 4px 12px rgba(30, 64, 175, 0.2)',
                transition: 'all 0.3s ease',
                textAlign: 'center',
                minHeight: '80px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 8px 20px rgba(30, 64, 175, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(30, 64, 175, 0.2)';
              }}
            >
              <div style={{ fontSize: '24px' }}>🧠</div>
              <div>KBC Quiz</div>
            </button>
            
            <button
              onClick={() => setCurrentView('ai-quiz')}
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: 'white',
                border: 'none',
                padding: '16px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Georgia, serif',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.2)',
                transition: 'all 0.3s ease',
                textAlign: 'center',
                minHeight: '80px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 8px 20px rgba(124, 58, 237, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.2)';
              }}
            >
              <div style={{ fontSize: '24px' }}>🤖</div>
              <div>AI Quiz</div>
            </button>
            
            <button
              onClick={() => setCurrentView('online-lobby')}
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                color: 'white',
                border: 'none',
                padding: '16px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Georgia, serif',
                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.2)',
                transition: 'all 0.3s ease',
                textAlign: 'center',
                minHeight: '80px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 8px 20px rgba(5, 150, 105, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.2)';
              }}
            >
              <div style={{ fontSize: '24px' }}>🌐</div>
              <div>Online Players</div>
            </button>
            
            {userRole !== 'student' && (
              <button
                onClick={() => setSelectedGame({
                  title: 'Triangle Simulator Test',
                  gameUrl: 'https://therisingschool.com/Learning%20Games/Triangle%20simulator.html'
                })}
                style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '16px 20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Georgia, serif',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  minHeight: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(220, 38, 38, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.2)';
                }}
              >
                <div style={{ fontSize: '24px' }}>🔧</div>
                <div>Test Modal</div>
              </button>
            )}
          </div>
          
          {/* Admin Controls Grid */}
          {['admin', 'admin1', 'teacher_admin_level1'].includes(userRole) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '20px'
            }}>
              <button
                onClick={() => setCurrentView('admin-dashboard')}
                style={{
                  background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '16px 20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Georgia, serif',
                  boxShadow: '0 4px 12px rgba(185, 28, 28, 0.2)',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  minHeight: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(185, 28, 28, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(185, 28, 28, 0.2)';
                }}
              >
                <div style={{ fontSize: '24px' }}>⚡</div>
                <div>Admin Quiz Control</div>
              </button>
              
              <button
                onClick={() => setCurrentView('questionManager')}
                style={{
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '16px 20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Georgia, serif',
                  boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  minHeight: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(217, 119, 6, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(217, 119, 6, 0.2)';
                }}
              >
                <div style={{ fontSize: '24px' }}>📝</div>
                <div>Manage Questions</div>
              </button>
            </div>
          )}
          
          {/* Add Game and Filter Controls */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: userRole === 'admin1' ? 'repeat(auto-fit, minmax(200px, 1fr))' : '1fr',
            gap: '16px',
            alignItems: 'center'
          }}>
            {userRole === 'admin1' && (
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '16px 20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Georgia, serif',
                  boxShadow: '0 4px 12px rgba(5, 150, 105, 0.2)',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  minHeight: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(5, 150, 105, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.2)';
                }}
              >
                <div style={{ fontSize: '24px' }}>➕</div>
                <div>Add External Game</div>
              </button>
            )}
            
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              padding: '16px 20px',
              borderRadius: '12px',
              border: '2px solid #cbd5e1',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              minHeight: '80px'
            }}>
              <div style={{ fontSize: '18px', color: '#1e40af', fontWeight: '600' }}>🔍</div>
              <select 
                value={filter} 
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  fontSize: '14px',
                  fontFamily: 'Georgia, serif',
                  fontWeight: '500',
                  background: 'white',
                  color: '#1e40af',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  maxWidth: '200px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#1e40af'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              >
                <option value="all">All Games</option>
                <option value="educational">Educational</option>
                <option value="fun">Fun Games</option>
                <option value="puzzle">Puzzle Games</option>
              </select>
            </div>
          </div>
        </div>

        {/* Available External Games */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            margin: '0 0 20px 0', 
            color: '#1e40af', 
            fontSize: '20px',
            fontFamily: 'Georgia, serif',
            fontWeight: '600',
            textAlign: 'center'
          }}>🎯 Subject Games</h3>
          {filteredGames.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '12px',
              border: '2px dashed #cbd5e1'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎮</div>
              <h3 style={{ 
                color: '#64748b', 
                fontFamily: 'Georgia, serif',
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>No games available</h3>
              <p style={{ 
                color: '#64748b',
                fontFamily: 'Georgia, serif',
                fontSize: '16px',
                margin: 0
              }}>
                {userRole === 'teacher' ? 'Click "Add External Game" to create your first game!' : 'Ask your teacher to add some games!'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
              gap: '12px'
            }}>
              {filteredGames.map(game => (
                <GameCard
                  key={game._id}
                  game={game}
                  userRole={userRole}
                  onDelete={userRole === 'admin1' ? handleDeleteGame : null}
                  onPlayGame={(game) => {
                    if (game.gameType === 'embed' || game.gameType === 'iframe') {
                      setSelectedGame(game);
                    } else {
                      window.open(game.gameUrl, '_blank');
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <AddGameModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddGame}
          availableClasses={availableClasses}
        />
      )}
      
      {selectedGame && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.9)', zIndex: 1000,
          display: 'flex', flexDirection: 'column'
        }}>
          <div style={{
            background: '#333', color: 'white', padding: '10px 20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <h3 style={{ margin: 0 }}>{selectedGame.title}</h3>
            <button onClick={() => setSelectedGame(null)} style={{
              background: '#ff4444', color: 'white', border: 'none',
              padding: '8px 16px', borderRadius: '4px', cursor: 'pointer'
            }}>
              ✕ Close
            </button>
          </div>
          <iframe 
            src={selectedGame.gameUrl}
            style={{ flex: 1, border: 'none', width: '100%' }}
            title={selectedGame.title}
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
};

export default GamesManager;